import * as React from "react";
import { Plus, Trash2 } from "lucide-react";
import { Text } from "../../../ui/components/Text";
import { Button } from "../../../ui/components/Button";
import { Modal } from "../../../ui/components/Modal";
import { Callout } from "../../../ui/components/Callout";
import { MediaUploader } from "../../../ui/components/MediaUploader";
import type { MediaEntry } from "../../../ui/components/MediaUploader";
import { LocalizedField } from "../../../ui/components/LocalizedField";
import { captureVideoThumbnail, captureVideoThumbnailFromUrl } from "../../lib/media/captureVideoThumbnail";
import {
  getYoutubeThumbnailUrl,
  getYoutubeVideoId,
  isYoutubeUrl,
} from "../../lib/media/youtube";
import { probeVideoDurationSeconds } from "../../lib/media/courseDuration";
import {
  createCertificationTraining,
  updateCertificationTraining,
  requestTrainingVideoUpload,
  requestTrainingThumbnailUpload,
  fetchTrainingForEdit,
} from "../../lib/api/certifications";
import type { CertificationTraining } from "../../lib/api/certifications";
import { suppressCertificationModuleRemoteRevision } from "../../lib/i18n/useCertificationModuleRemoteRevision";
import {
  batchPatchLocalizationDefinitions,
  fetchAdminLanguages,
  fetchModulesForLanguage,
  type Language,
} from "../../lib/api/localization";
import { useI18n } from "../../lib/i18n";
import { newId, questionChoiceKey } from "../../lib/i18n/contentKeys";
import {
  allDrafts,
  collectDirtyUpdates,
  createEmptyTrainingDraft,
  defaultLangCode,
  emptyQuestionDraft,
  findDuplicateQuestionTextId,
  getLocDisplayValue,
  hydrateTrainingDraft,
  markRequiredDirty,
  mergeDefinitionsIntoDraft,
  questionsToApiPayload,
  setLocValue,
  type LocFieldDraft,
  type TrainingLocDraft,
} from "../../lib/i18n/trainingLocalizedDraft";
import * as s from "./TrainingFormModal.style.css";

export interface TrainingFormModalProps {
  open: boolean;
  onClose: () => void;
  moduleId: string;
  accessToken: string;
  existing?: CertificationTraining;
  /** Suggested step number when creating a new training. */
  nextStepNumber?: number;
  onSaved: (training: CertificationTraining) => void;
}

interface StagedVideo {
  file: File;
  previewUrl?: string;
  thumbnailBlob?: Blob;
}

async function uploadTrainingVideoFile(
  accessToken: string,
  trainingId: string,
  file: File,
  failMessage: string,
): Promise<string> {
  const instructions = await requestTrainingVideoUpload(accessToken, trainingId, file.type);
  const isPresigned = instructions.uploadUrl.includes("X-Amz-Signature");
  const headers: Record<string, string> = { "Content-Type": file.type };
  if (!isPresigned) {
    if (instructions.key) headers["X-Video-Key"] = instructions.key;
    if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
  }
  const res = await fetch(instructions.uploadUrl, { method: "PUT", body: file, headers });
  if (!res.ok) {
    throw new Error(failMessage);
  }
  return instructions.videoUrl;
}

async function uploadTrainingThumbnailFile(
  accessToken: string,
  trainingId: string,
  blob: Blob,
  failMessage: string,
): Promise<string> {
  const contentType = "image/jpeg";
  const instructions = await requestTrainingThumbnailUpload(accessToken, trainingId, contentType);
  const isPresigned = instructions.uploadUrl.includes("X-Amz-Signature");
  const headers: Record<string, string> = { "Content-Type": contentType };
  if (!isPresigned) {
    if (instructions.key) headers["X-Avatar-Key"] = instructions.key;
    if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
  }
  const res = await fetch(instructions.uploadUrl, { method: "PUT", body: blob, headers });
  if (!res.ok) {
    throw new Error(failMessage);
  }
  return instructions.avatarUrl;
}

/** Hotlinked YouTube poster URL when the video is a YouTube link; otherwise undefined. */
function youtubeThumbnailForVideoUrl(videoUrl: string | null | undefined): string | undefined {
  const id = videoUrl ? getYoutubeVideoId(videoUrl) : null;
  return id ? getYoutubeThumbnailUrl(id) : undefined;
}

/** Client-side duration hint for hosted files (YouTube resolves server-side). */
async function clientDurationHintSeconds(
  videoUrl: string | null | undefined,
): Promise<number | undefined> {
  const seconds = await probeVideoDurationSeconds(videoUrl);
  return seconds != null && seconds > 0 ? Math.round(seconds) : undefined;
}

function formatTrainingSaveError(
  err: unknown,
  t: (key: string) => string,
): string {
  const raw =
    err instanceof Error
      ? err.message
      : typeof err === "object" && err && "message" in err
        ? String((err as { message: unknown }).message)
        : "";
  if (/expected string, received null/i.test(raw)) {
    return t("pro.system.certification.training.error.invalidOptionalField");
  }
  if (/enter a valid video url/i.test(raw) || /invalid url/i.test(raw) && /video/i.test(raw)) {
    return t("pro.system.certification.training.error.invalidVideoUrl");
  }
  return raw.trim() || t("pro.system.certification.training.error.save");
}

/** Resolve a poster frame to upload — pending ref, staged blob preview, or capture from video URL. */
async function resolveTrainingThumbnailBlob(
  newItem: MediaEntry,
  pendingRef: React.MutableRefObject<Blob | null>,
): Promise<Blob | undefined> {
  // YouTube posters are hotlinked — never capture/upload a blob.
  if (isYoutubeUrl(newItem.url)) {
    pendingRef.current = null;
    return undefined;
  }
  if (pendingRef.current) {
    const blob = pendingRef.current;
    pendingRef.current = null;
    return blob;
  }
  if (newItem.previewUrl?.startsWith("blob:")) {
    try {
      const res = await fetch(newItem.previewUrl);
      return await res.blob();
    } catch {
      // fall through
    }
  }
  if (newItem.url && /^https?:\/\//.test(newItem.url)) {
    try {
      return await captureVideoThumbnailFromUrl(newItem.url);
    } catch {
      // fall through
    }
  }
  return undefined;
}

export function TrainingFormModal({
  open,
  onClose,
  moduleId,
  accessToken,
  existing,
  nextStepNumber,
  onSaved,
}: TrainingFormModalProps) {
  const { t } = useI18n();
  const isEdit = !!existing;

  const [locDraft, setLocDraft] = React.useState<TrainingLocDraft>(() =>
    createEmptyTrainingDraft(existing?.id ?? newId(), "en"),
  );
  const [languages, setLanguages] = React.useState<Language[]>([]);
  const [defaultLang, setDefaultLang] = React.useState("en");
  const [defsByLang, setDefsByLang] = React.useState<Record<string, Record<string, string>>>({});
  const [fieldLang, setFieldLang] = React.useState<Record<string, string>>({});
  const [currentVideoUrl, setCurrentVideoUrl] = React.useState<string | null>(existing?.videoUrl ?? null);
  const [currentThumbnailUrl, setCurrentThumbnailUrl] = React.useState<string | null>(
    existing?.thumbnailUrl ?? null,
  );
  const [stagedVideo, setStagedVideo] = React.useState<StagedVideo | null>(null);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [loadingExisting, setLoadingExisting] = React.useState(false);

  const activeTraining = existing;
  const pendingCreateVideoRef = React.useRef<StagedVideo | null>(null);
  const pendingThumbnailBlobRef = React.useRef<Blob | null>(null);
  const pendingPastedVideoUrlRef = React.useRef<string | null>(null);
  const loadedLangsRef = React.useRef<Set<string>>(new Set());

  const revokePreviewUrl = React.useCallback((previewUrl?: string) => {
    if (previewUrl?.startsWith("blob:")) {
      URL.revokeObjectURL(previewUrl);
    }
  }, []);

  const clearPendingCreateVideo = React.useCallback(() => {
    const pending = pendingCreateVideoRef.current;
    if (!pending) return;
    revokePreviewUrl(pending.previewUrl);
    pendingCreateVideoRef.current = null;
  }, [revokePreviewUrl]);

  const ensureLangDefinitions = React.useCallback(
    async (lang: string) => {
      if (loadedLangsRef.current.has(lang)) return defsByLang[lang] ?? {};
      try {
        const modules = await fetchModulesForLanguage(accessToken, lang);
        const cert = modules.find((m) => m.context === "certification");
        const definitions = cert?.definitions ?? {};
        loadedLangsRef.current.add(lang);
        setDefsByLang((prev) => ({ ...prev, [lang]: definitions }));
        setLocDraft((prev) => mergeDefinitionsIntoDraft(prev, lang, definitions));
        return definitions;
      } catch {
        loadedLangsRef.current.add(lang);
        return {};
      }
    },
    [accessToken, defsByLang],
  );

  React.useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const trainingId = existing?.id ?? newId();
    setCurrentVideoUrl(existing?.videoUrl ?? null);
    setCurrentThumbnailUrl(existing?.thumbnailUrl ?? null);
    clearPendingCreateVideo();
    setStagedVideo(null);
    pendingPastedVideoUrlRef.current = null;
    pendingThumbnailBlobRef.current = null;
    setError(null);
    setFieldLang({});
    loadedLangsRef.current = new Set();
    setDefsByLang({});

    (async () => {
      setLoadingExisting(true);
      try {
        const langs = await fetchAdminLanguages(accessToken);
        if (cancelled) return;
        setLanguages(langs);
        const defLang = defaultLangCode(langs);
        setDefaultLang(defLang);

        const modules = await fetchModulesForLanguage(accessToken, defLang);
        if (cancelled) return;
        const cert = modules.find((m) => m.context === "certification");
        const definitions = cert?.definitions ?? {};
        loadedLangsRef.current.add(defLang);
        setDefsByLang({ [defLang]: definitions });

        if (existing) {
          const full = await fetchTrainingForEdit(accessToken, existing.id);
          if (cancelled) return;
          setCurrentVideoUrl(full.videoUrl ?? null);
          setCurrentThumbnailUrl(full.thumbnailUrl ?? null);
          setLocDraft(
            hydrateTrainingDraft({
              trainingId: full.id,
              title: full.title,
              section: full.section,
              transcript: full.transcript,
              videoCommentary: full.videoCommentary,
              questions: full.questions?.map((q) => ({
                id: q.id,
                text: q.text,
                choices: [...q.choices],
                correctChoiceIndex: q.correctChoiceIndex,
                sortOrder: q.sortOrder,
              })),
              defaultLang: defLang,
              definitions,
            }),
          );
        } else {
          setLocDraft(createEmptyTrainingDraft(trainingId, defLang));
        }
      } catch {
        if (!cancelled) {
          setLocDraft(createEmptyTrainingDraft(trainingId, "en"));
        }
      } finally {
        if (!cancelled) setLoadingExisting(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, existing, accessToken, nextStepNumber, clearPendingCreateVideo]);

  const updateLoc = React.useCallback(
    (updater: (prev: TrainingLocDraft) => TrainingLocDraft) => {
      setLocDraft(updater);
      setError(null);
    },
    [],
  );

  const patchLocField = (
    pick: (d: TrainingLocDraft) => LocFieldDraft,
    replace: (d: TrainingLocDraft, next: LocFieldDraft) => TrainingLocDraft,
    lang: string,
    value: string,
  ) => {
    updateLoc((prev) => replace(prev, setLocValue(pick(prev), lang, value)));
  };

  const addQuestion = () => {
    updateLoc((prev) => ({
      ...prev,
      questions: [...prev.questions, emptyQuestionDraft(prev.questions.length, defaultLang)],
    }));
  };

  const removeQuestion = (index: number) => {
    updateLoc((prev) => ({
      ...prev,
      questions: prev.questions
        .filter((_, i) => i !== index)
        .map((q, i) => ({ ...q, sortOrder: i })),
    }));
  };

  const updateQuestionLoc = (index: number, lang: string, value: string) => {
    updateLoc((prev) => ({
      ...prev,
      questions: prev.questions.map((q, i) =>
        i === index ? { ...q, text: setLocValue(q.text, lang, value) } : q,
      ),
    }));
  };

  const addChoice = (qIndex: number) => {
    updateLoc((prev) => ({
      ...prev,
      questions: prev.questions.map((q, i) => {
        if (i !== qIndex) return q;
        const choiceId = newId();
        return {
          ...q,
          choices: [
            ...q.choices,
            {
              id: choiceId,
              loc: {
                key: questionChoiceKey(q.id, choiceId),
                valuesByLang: { [defaultLang]: "" },
                dirtyLangs: [],
              },
            },
          ],
        };
      }),
    }));
  };

  const removeChoice = (qIndex: number, choiceIndex: number) => {
    updateLoc((prev) => ({
      ...prev,
      questions: prev.questions.map((q, i) => {
        if (i !== qIndex) return q;
        const choices = q.choices.filter((_, ci) => ci !== choiceIndex);
        let correctChoiceIndex = q.correctChoiceIndex;
        if (choiceIndex === q.correctChoiceIndex) correctChoiceIndex = 0;
        else if (choiceIndex < q.correctChoiceIndex) correctChoiceIndex -= 1;
        return { ...q, choices, correctChoiceIndex };
      }),
    }));
  };

  const updateChoiceLoc = (qIndex: number, choiceIndex: number, lang: string, value: string) => {
    updateLoc((prev) => ({
      ...prev,
      questions: prev.questions.map((q, i) => {
        if (i !== qIndex) return q;
        return {
          ...q,
          choices: q.choices.map((c, ci) =>
            ci === choiceIndex ? { ...c, loc: setLocValue(c.loc, lang, value) } : c,
          ),
        };
      }),
    }));
  };

  const setCorrectChoice = (qIndex: number, choiceIndex: number) => {
    updateLoc((prev) => ({
      ...prev,
      questions: prev.questions.map((q, i) =>
        i === qIndex ? { ...q, correctChoiceIndex: choiceIndex } : q,
      ),
    }));
  };

  const activeLangFor = (key: string) => fieldLang[key] ?? defaultLang;

  const onFieldLangChange = (key: string, lang: string) => {
    setFieldLang((prev) => ({ ...prev, [key]: lang }));
    void ensureLangDefinitions(lang);
  };

  const prepareVideoFromFile = React.useCallback(async (file: File): Promise<StagedVideo> => {
    let thumbnailBlob: Blob | undefined;
    let previewUrl: string | undefined;
    try {
      thumbnailBlob = await captureVideoThumbnail(file);
      previewUrl = URL.createObjectURL(thumbnailBlob);
    } catch {
      previewUrl = URL.createObjectURL(file);
    }
    return { file, previewUrl, thumbnailBlob };
  }, []);

  const videoItems: MediaEntry[] = React.useMemo(() => {
    if (currentVideoUrl) {
      return [{
        id: activeTraining?.id ?? "video",
        url: currentVideoUrl,
        previewUrl:
          currentThumbnailUrl
          ?? youtubeThumbnailForVideoUrl(currentVideoUrl)
          ?? undefined,
      }];
    }
    return [];
  }, [currentVideoUrl, currentThumbnailUrl, activeTraining?.id]);

  const uploadVideoFile = React.useCallback(
    async (file: File): Promise<{ url: string; previewUrl?: string }> => {
      if (activeTraining) {
        let previewUrl: string | undefined;
        try {
          const thumbnailBlob = await captureVideoThumbnail(file);
          pendingThumbnailBlobRef.current = thumbnailBlob;
          previewUrl = URL.createObjectURL(thumbnailBlob);
        } catch {
          pendingThumbnailBlobRef.current = null;
        }
        const videoUrl = await uploadTrainingVideoFile(
          accessToken,
          activeTraining.id,
          file,
          t("pro.system.certification.training.error.uploadVideo"),
        );
        return { url: videoUrl, previewUrl };
      }

      clearPendingCreateVideo();
      const pending = await prepareVideoFromFile(file);
      pendingCreateVideoRef.current = pending;
      return { url: pending.previewUrl ?? "", previewUrl: pending.previewUrl };
    },
    [accessToken, activeTraining, clearPendingCreateVideo, prepareVideoFromFile, t],
  );

  const persistVideoAssets = React.useCallback(
    async (trainingId: string, video: StagedVideo) => {
      const videoUrl = await uploadTrainingVideoFile(
        accessToken,
        trainingId,
        video.file,
        t("pro.system.certification.training.error.uploadVideo"),
      );
      let thumbnailUrl: string | undefined;
      if (video.thumbnailBlob) {
        thumbnailUrl = await uploadTrainingThumbnailFile(
          accessToken,
          trainingId,
          video.thumbnailBlob,
          t("pro.system.certification.training.error.uploadThumbnail"),
        );
      }
      const videoDurationSeconds = await clientDurationHintSeconds(videoUrl);
      return updateCertificationTraining(accessToken, trainingId, {
        videoUrl,
        ...(thumbnailUrl ? { thumbnailUrl } : {}),
        ...(videoDurationSeconds != null ? { videoDurationSeconds } : {}),
      });
    },
    [accessToken, t],
  );

  const handleVideoApply = React.useCallback(
    async (newItems: MediaEntry[]) => {
      const newItem = newItems[0];
      if (!newItem?.url) return;

      try {
        pendingPastedVideoUrlRef.current = null;

        if (activeTraining && !pendingCreateVideoRef.current) {
          const ytThumb = youtubeThumbnailForVideoUrl(newItem.url);
          let thumbnailUrl: string | undefined = ytThumb;
          if (!ytThumb) {
            const thumbnailBlob = await resolveTrainingThumbnailBlob(newItem, pendingThumbnailBlobRef);
            if (thumbnailBlob) {
              thumbnailUrl = await uploadTrainingThumbnailFile(
                accessToken,
                activeTraining.id,
                thumbnailBlob,
                t("pro.system.certification.training.error.uploadThumbnail"),
              );
            }
          } else {
            pendingThumbnailBlobRef.current = null;
          }
          const videoDurationSeconds = await clientDurationHintSeconds(newItem.url);
          const updated = await updateCertificationTraining(accessToken, activeTraining.id, {
            videoUrl: newItem.url,
            ...(thumbnailUrl ? { thumbnailUrl } : {}),
            ...(videoDurationSeconds != null ? { videoDurationSeconds } : {}),
          });
          const persistedThumb = updated.thumbnailUrl ?? thumbnailUrl ?? null;
          setCurrentVideoUrl(updated.videoUrl ?? newItem.url);
          setCurrentThumbnailUrl(persistedThumb);
          onSaved({ ...updated, thumbnailUrl: persistedThumb });
          return;
        }

        const pending = pendingCreateVideoRef.current;
        if (pending) {
          pendingCreateVideoRef.current = null;
          setStagedVideo(pending);
          setCurrentVideoUrl(newItem.url);
          setCurrentThumbnailUrl(newItem.previewUrl ?? pending.previewUrl ?? null);
          return;
        }

        // Create flow: pasted external URL (no file upload) — including YouTube hotlinks.
        setCurrentVideoUrl(newItem.url);
        setCurrentThumbnailUrl(
          youtubeThumbnailForVideoUrl(newItem.url) ?? newItem.previewUrl ?? null,
        );
      } catch (err: unknown) {
        const message =
          err instanceof Error ? err.message : t("pro.system.certification.training.error.saveVideo");
        setError(message);
        throw err;
      }
    },
    [activeTraining, accessToken, existing?.videoUrl, onSaved, t],
  );

  const handleStagedVideoUrl = React.useCallback((url: string) => {
    pendingPastedVideoUrlRef.current = url;
    setError(null);
  }, []);

  const handleStagedVideoThumbnail = React.useCallback((blob: Blob | null) => {
    pendingThumbnailBlobRef.current = blob;
  }, []);

  const handleVideoModalDismiss = React.useCallback(() => {
    clearPendingCreateVideo();
    pendingThumbnailBlobRef.current = null;
    pendingPastedVideoUrlRef.current = null;
  }, [clearPendingCreateVideo]);

  const handleVideoRemove = React.useCallback(
    async (_id: string) => {
      if (stagedVideo) {
        revokePreviewUrl(stagedVideo.previewUrl);
        setStagedVideo(null);
        setCurrentVideoUrl(null);
        setCurrentThumbnailUrl(null);
        return;
      }
      if (!activeTraining) return;
      try {
        const updated = await updateCertificationTraining(accessToken, activeTraining.id, {
          videoUrl: null,
          thumbnailUrl: null,
        });
        setCurrentVideoUrl(updated.videoUrl ?? null);
        setCurrentThumbnailUrl(updated.thumbnailUrl ?? null);
        onSaved(updated);
      } catch (err: any) {
        setError(err?.message ?? t("pro.system.certification.training.error.removeVideo"));
      }
    },
    [activeTraining, stagedVideo, accessToken, onSaved, revokePreviewUrl, t],
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const titleVal = locDraft.title.valuesByLang[defaultLang]?.trim() ?? "";
    if (!titleVal) {
      setError(t("pro.system.certification.training.validation.titleRequired"));
      return;
    }
    for (const q of locDraft.questions) {
      if (!(q.text.valuesByLang[defaultLang]?.trim())) {
        setError(t("pro.system.certification.training.validation.questionTextRequired"));
        return;
      }
      if (q.choices.some((c) => !(c.loc.valuesByLang[defaultLang]?.trim()))) {
        setError(t("pro.system.certification.training.validation.choiceTextRequired"));
        return;
      }
      if (q.choices.length < 2) {
        setError(t("pro.system.certification.training.validation.minChoices"));
        return;
      }
    }
    if (findDuplicateQuestionTextId(locDraft.questions, defaultLang)) {
      setError(t("pro.system.certification.training.validation.duplicateQuestionText"));
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const draftForSave = markRequiredDirty(locDraft, defaultLang);
      const questionPayload = questionsToApiPayload(draftForSave.questions, defaultLang);
      const sectionKey = draftForSave.section.valuesByLang[defaultLang]?.trim()
        ? draftForSave.section.key
        : undefined;
      const transcriptKey = draftForSave.transcript.valuesByLang[defaultLang]?.trim()
        ? draftForSave.transcript.key
        : undefined;
      const videoCommentaryKey = draftForSave.videoCommentary.valuesByLang[defaultLang]?.trim()
        ? draftForSave.videoCommentary.key
        : undefined;
      const effectiveVideoUrl = currentVideoUrl ?? pendingPastedVideoUrlRef.current;
      const basePayload = {
        id: draftForSave.trainingId,
        moduleId,
        title: draftForSave.title.key,
        ...(sectionKey ? { section: sectionKey } : {}),
        // Step order is owned by the Course trainings strip (position in the row).
        ...(isEdit
          ? {}
          : { stepNumber: nextStepNumber ?? 1, sortOrder: nextStepNumber ?? 1 }),
        ...(transcriptKey ? { transcript: transcriptKey } : {}),
        ...(videoCommentaryKey ? { videoCommentary: videoCommentaryKey } : {}),
        ...(effectiveVideoUrl ? { videoUrl: effectiveVideoUrl } : {}),
        ...(currentThumbnailUrl ? { thumbnailUrl: currentThumbnailUrl } : {}),
        questions: questionPayload,
      };

      let saved: CertificationTraining;
      suppressCertificationModuleRemoteRevision(2500);
      if (isEdit) {
        const trainingId = existing!.id;
        const videoPatch: Partial<
          Pick<CertificationTraining, "videoUrl" | "thumbnailUrl" | "videoDurationSeconds">
        > = {};
        if (effectiveVideoUrl !== (existing?.videoUrl ?? null)) {
          videoPatch.videoUrl = effectiveVideoUrl;
          const videoDurationSeconds = await clientDurationHintSeconds(effectiveVideoUrl);
          if (videoDurationSeconds != null) {
            videoPatch.videoDurationSeconds = videoDurationSeconds;
          }
        }
        const ytThumb = youtubeThumbnailForVideoUrl(effectiveVideoUrl);
        if (ytThumb && effectiveVideoUrl !== (existing?.videoUrl ?? null)) {
          videoPatch.thumbnailUrl = ytThumb;
          pendingThumbnailBlobRef.current = null;
        } else if (!ytThumb) {
          const pendingThumb = pendingThumbnailBlobRef.current;
          if (pendingThumb) {
            videoPatch.thumbnailUrl = await uploadTrainingThumbnailFile(
              accessToken,
              trainingId,
              pendingThumb,
              t("pro.system.certification.training.error.uploadThumbnail"),
            );
            pendingThumbnailBlobRef.current = null;
          } else if (effectiveVideoUrl && effectiveVideoUrl !== (existing?.videoUrl ?? null)) {
            const blob = await resolveTrainingThumbnailBlob(
              { id: trainingId, url: effectiveVideoUrl, previewUrl: currentThumbnailUrl ?? undefined },
              pendingThumbnailBlobRef,
            );
            if (blob) {
              videoPatch.thumbnailUrl = await uploadTrainingThumbnailFile(
                accessToken,
                trainingId,
                blob,
                t("pro.system.certification.training.error.uploadThumbnail"),
              );
            }
          } else if (
            currentThumbnailUrl
            && !currentThumbnailUrl.startsWith("blob:")
            && currentThumbnailUrl !== (existing?.thumbnailUrl ?? null)
          ) {
            videoPatch.thumbnailUrl = currentThumbnailUrl;
          }
        }
        saved = await updateCertificationTraining(accessToken, trainingId, {
          ...basePayload,
          section: draftForSave.section.valuesByLang[defaultLang]?.trim()
            ? draftForSave.section.key
            : null,
          transcript: draftForSave.transcript.valuesByLang[defaultLang]?.trim()
            ? draftForSave.transcript.key
            : null,
          videoCommentary: draftForSave.videoCommentary.valuesByLang[defaultLang]?.trim()
            ? draftForSave.videoCommentary.key
            : null,
          ...videoPatch,
          questions: questionPayload,
        });
        pendingPastedVideoUrlRef.current = null;
      } else {
        saved = await createCertificationTraining(accessToken, {
          ...basePayload,
          questions: questionPayload,
        });
      }

      const updates = collectDirtyUpdates(allDrafts(draftForSave)).filter(
        (u) => Object.keys(u.set).length > 0,
      );
      if (updates.length > 0) {
        await batchPatchLocalizationDefinitions(accessToken, {
          context: "certification",
          updates,
        });
      }

      if (stagedVideo) {
        saved = await persistVideoAssets(saved.id, stagedVideo);
        setStagedVideo(null);
      } else if (!isEdit && effectiveVideoUrl) {
        const ytThumb = youtubeThumbnailForVideoUrl(effectiveVideoUrl);
        let thumbnailUrl: string | undefined = ytThumb;
        if (!ytThumb) {
          const thumbBlob = pendingThumbnailBlobRef.current;
          if (thumbBlob) {
            thumbnailUrl = await uploadTrainingThumbnailFile(
              accessToken,
              saved.id,
              thumbBlob,
              t("pro.system.certification.training.error.uploadThumbnail"),
            );
            pendingThumbnailBlobRef.current = null;
          } else if (
            currentThumbnailUrl
            && !currentThumbnailUrl.startsWith("blob:")
          ) {
            thumbnailUrl = currentThumbnailUrl;
          }
        } else {
          pendingThumbnailBlobRef.current = null;
        }
        const videoDurationSeconds = await clientDurationHintSeconds(effectiveVideoUrl);
        saved = await updateCertificationTraining(accessToken, saved.id, {
          videoUrl: effectiveVideoUrl,
          ...(thumbnailUrl ? { thumbnailUrl } : {}),
          ...(videoDurationSeconds != null ? { videoDurationSeconds } : {}),
        });
        pendingPastedVideoUrlRef.current = null;
      }

      onSaved({
        ...saved,
        videoUrl: saved.videoUrl ?? effectiveVideoUrl ?? currentVideoUrl ?? null,
        thumbnailUrl: saved.thumbnailUrl ?? null,
      });
      onClose();
    } catch (err: unknown) {
      setError(formatTrainingSaveError(err, t));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open={open} setOpen={(v) => { if (!v) onClose(); }} minWidth="720px" maxWidth="900px" closeOnClickOff>
      <Modal.Form onSubmit={handleSubmit}>
        <Modal.Header>
          <Text level="h3" weight="bold">
            {isEdit
              ? t("pro.system.certification.training.modal.edit")
              : t("pro.system.certification.training.modal.add")}
          </Text>
        </Modal.Header>
        <Modal.Body>
          <div className={s.form}>

          <LocalizedField
            label={t("pro.system.certification.training.form.title")}
            locKey={locDraft.title.key}
            languages={languages}
            selectedLang={activeLangFor(locDraft.title.key)}
            onSelectedLangChange={(lang) => onFieldLangChange(locDraft.title.key, lang)}
            value={getLocDisplayValue(locDraft.title, activeLangFor(locDraft.title.key), defaultLang)}
            referenceValue={locDraft.title.valuesByLang[defaultLang]}
            onChange={(lang, value) =>
              patchLocField(
                (d) => d.title,
                (d, next) => ({ ...d, title: next }),
                lang,
                value,
              )
            }
            onLanguageFocus={(lang) => void ensureLangDefinitions(lang)}
            disabled={saving || loadingExisting}
            required
            placeholder={t("pro.system.certification.training.form.titlePlaceholder")}
            autoFocus
            defaultLang={defaultLang}
          />

          <div className={s.fieldGrid}>
            <LocalizedField
              label={t("pro.system.certification.training.form.section")}
              locKey={locDraft.section.key}
              languages={languages}
              selectedLang={activeLangFor(locDraft.section.key)}
              onSelectedLangChange={(lang) => onFieldLangChange(locDraft.section.key, lang)}
              value={getLocDisplayValue(locDraft.section, activeLangFor(locDraft.section.key), defaultLang)}
              referenceValue={locDraft.section.valuesByLang[defaultLang]}
              onChange={(lang, value) =>
                patchLocField(
                  (d) => d.section,
                  (d, next) => ({ ...d, section: next }),
                  lang,
                  value,
                )
              }
              onLanguageFocus={(lang) => void ensureLangDefinitions(lang)}
              disabled={saving || loadingExisting}
              placeholder={t("pro.system.certification.training.form.sectionPlaceholder")}
              defaultLang={defaultLang}
            />
          </div>

          <LocalizedField
            label={t("pro.system.certification.training.form.transcript")}
            locKey={locDraft.transcript.key}
            languages={languages}
            selectedLang={activeLangFor(locDraft.transcript.key)}
            onSelectedLangChange={(lang) => onFieldLangChange(locDraft.transcript.key, lang)}
            value={getLocDisplayValue(
              locDraft.transcript,
              activeLangFor(locDraft.transcript.key),
              defaultLang,
            )}
            referenceValue={locDraft.transcript.valuesByLang[defaultLang]}
            onChange={(lang, value) =>
              patchLocField(
                (d) => d.transcript,
                (d, next) => ({ ...d, transcript: next }),
                lang,
                value,
              )
            }
            onLanguageFocus={(lang) => void ensureLangDefinitions(lang)}
            disabled={saving || loadingExisting}
            multiline
            rows={4}
            placeholder={t("pro.system.certification.training.form.transcriptPlaceholder")}
            defaultLang={defaultLang}
          />

          <LocalizedField
            label={t("pro.system.certification.training.form.videoCommentary")}
            locKey={locDraft.videoCommentary.key}
            languages={languages}
            selectedLang={activeLangFor(locDraft.videoCommentary.key)}
            onSelectedLangChange={(lang) => onFieldLangChange(locDraft.videoCommentary.key, lang)}
            value={getLocDisplayValue(
              locDraft.videoCommentary,
              activeLangFor(locDraft.videoCommentary.key),
              defaultLang,
            )}
            referenceValue={locDraft.videoCommentary.valuesByLang[defaultLang]}
            onChange={(lang, value) =>
              patchLocField(
                (d) => d.videoCommentary,
                (d, next) => ({ ...d, videoCommentary: next }),
                lang,
                value,
              )
            }
            onLanguageFocus={(lang) => void ensureLangDefinitions(lang)}
            disabled={saving || loadingExisting}
            multiline
            rows={3}
            placeholder={t("pro.system.certification.training.form.videoCommentaryPlaceholder")}
            defaultLang={defaultLang}
          />

          <div>
            <label className={s.label}>{t("pro.system.certification.training.video")}</label>
            <MediaUploader
              mediaType="video"
              allowYoutube
              items={videoItems}
              maxItems={1}
              shape="rectangle"
              uploadFile={uploadVideoFile}
              onApply={handleVideoApply}
              onStagedUrl={handleStagedVideoUrl}
              onStagedVideoThumbnail={handleStagedVideoThumbnail}
              onChange={() => {}}
              onRemove={handleVideoRemove}
              onModalDismiss={handleVideoModalDismiss}
              onError={setError}
            />
          </div>

          <div className={s.questionsSection}>
            <div className={s.questionsHeader}>
              <label className={s.label}>{t("pro.system.certification.training.questions.title")}</label>
              <Button type="button" color="primary" variant="outline" size="sm" iconLeft={<Plus size={12} />} onClick={addQuestion} disabled={saving || loadingExisting}>
                {t("pro.system.certification.training.questions.add")}
              </Button>
            </div>

            {locDraft.questions.length === 0 && (
              <Text level="body-small" className={s.hint}>
                {t("pro.system.certification.training.questions.empty")}
              </Text>
            )}

            {locDraft.questions.map((q, qIndex) => (
              <div key={q.id} className={s.questionBlock}>
                <div className={s.questionHeader}>
                  <div className={s.questionBody}>
                    <LocalizedField
                      label={t("pro.system.certification.training.questions.questionN", { n: qIndex + 1 })}
                      locKey={q.text.key}
                      languages={languages}
                      selectedLang={activeLangFor(q.text.key)}
                      onSelectedLangChange={(lang) => onFieldLangChange(q.text.key, lang)}
                      value={getLocDisplayValue(q.text, activeLangFor(q.text.key), defaultLang)}
                      referenceValue={q.text.valuesByLang[defaultLang]}
                      onChange={(lang, value) => updateQuestionLoc(qIndex, lang, value)}
                      onLanguageFocus={(lang) => void ensureLangDefinitions(lang)}
                      disabled={saving || loadingExisting}
                      placeholder={t("pro.system.certification.training.questions.questionText")}
                      defaultLang={defaultLang}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => removeQuestion(qIndex)}
                    disabled={saving}
                    className={s.removeQuestionBtn}
                    title={t("pro.system.certification.training.questions.removeQuestion")}
                  >
                    <Trash2 size={16} />
                  </button>
                </div>

                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  {q.choices.map((choice, cIndex) => (
                    <div key={choice.id} className={s.choiceRow}>
                      <input
                        type="radio"
                        name={`correct-${q.id}`}
                        checked={q.correctChoiceIndex === cIndex}
                        onChange={() => setCorrectChoice(qIndex, cIndex)}
                        disabled={saving}
                        title={t("pro.system.certification.training.questions.markCorrect")}
                      />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <LocalizedField
                          label={t("pro.system.certification.training.questions.choiceN", { n: cIndex + 1 })}
                          locKey={choice.loc.key}
                          languages={languages}
                          selectedLang={activeLangFor(choice.loc.key)}
                          onSelectedLangChange={(lang) => onFieldLangChange(choice.loc.key, lang)}
                          value={getLocDisplayValue(
                            choice.loc,
                            activeLangFor(choice.loc.key),
                            defaultLang,
                          )}
                          referenceValue={choice.loc.valuesByLang[defaultLang]}
                          onChange={(lang, value) => updateChoiceLoc(qIndex, cIndex, lang, value)}
                          onLanguageFocus={(lang) => void ensureLangDefinitions(lang)}
                          disabled={saving || loadingExisting}
                          placeholder={t("pro.system.certification.training.questions.choiceN", { n: cIndex + 1 })}
                          defaultLang={defaultLang}
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => removeChoice(qIndex, cIndex)}
                        disabled={saving || q.choices.length <= 2}
                        className={[s.removeChoiceBtn, q.choices.length <= 2 ? s.removeChoiceBtnDisabled : ""].filter(Boolean).join(" ")}
                        title={t("pro.system.certification.training.questions.removeChoice")}
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  ))}
                  <Button type="button" color="neutral" variant="ghost" size="sm" iconLeft={<Plus size={12} />} onClick={() => addChoice(qIndex)} disabled={saving}>
                    {t("pro.system.certification.training.questions.addChoice")}
                  </Button>
                </div>
              </div>
            ))}
          </div>
          </div>
        </Modal.Body>
        <Modal.Footer className={s.footer}>
          {error ? (
            <div className={s.footerError}>
              <Callout variant="error">{error}</Callout>
            </div>
          ) : null}
          <div className={s.footerActions}>
            <Button type="button" color="primary" variant="outline" onClick={onClose} disabled={saving}>
              {t("shared.common.cancel")}
            </Button>
            <Button type="submit" color="primary" variant="solid" loading={saving}>
              {isEdit
                ? t("pro.system.certification.training.saveChanges")
                : t("pro.system.certification.training.addTraining")}
            </Button>
          </div>
        </Modal.Footer>
      </Modal.Form>
    </Modal>
  );
}
